import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormControl,
    RadioGroup,
    Radio,
    Typography,
} from '@mui/material';

import { ReactComponent as BnplInstalmentsIcon } from '@/img/icons/bnpl-instalments.svg';
import { ReactComponent as FreeCancelationIcon } from '@/img/elal/free-cancellation-icon.svg';
import { ReactComponent as ElalCheckmarkIcon } from '@/img/elal/matmid-points-icon.svg';
import { ReactComponent as InfoIcon } from '@/img/reservation_page/info.svg';
import { computeBnplAmounts } from '@/utils/PayLater';

import React from 'react';
import createClass from 'create-react-class';
import _ from 'lodash';
import moment from 'moment';

import ReservationStore from '../../../stores/ReservationStore';
import PaymentStore from '../../../stores/PaymentStore';
import ProfileStore from '../../../stores/ProfileStore';
import SearchStore from '../../../stores/SearchStore';
import HotelStore from '../../../stores/HotelStore';

import ReservationActions from '../../../actions/ReservationActions';
import PointsActions from '../../../actions/PointsActions';

import CompanyPolicyComponent from './CompanyPolicyComponent.react';
import CreditCardComponent from './CreditCardComponent.react';
import PayAtHotelComponent from './PayAtHotelComponent.react';

import Analytics from 'arbitrip-common/client/analytics';
import {
    BNPL_CURRENCY_USD,
    BNPL_SELECTED_OPTION,
    CHECKOUT_FLOW_VARIANT,
} from 'arbitrip-common/client/analytics/constants/bnpl';

import ReservationConstants from '../../../constants/ReservationConstants';
import PaymentConstants from '../../../constants/PaymentConstants';
import ProfileActions from '../../../actions/ProfileActions';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';

import { isAccountPaymentAvailable } from 'arbitrip-common/general/utils/AccountPayment';
import Currencies from 'arbitrip-common/general/utils/Currencies';

import { withTranslation, Trans } from 'react-i18next';
import NewRedeemPoints from './NewRedeemPoints.react';
import ExpediaCreditCardComponent from './ExpediaCreditCardComponent.react';
import PayThreeDSConnectorHelper from 'arbitrip-common/general/utils/PayThreeDSConnectorHelper';
import MaterialDesignDialog from '../../general/MaterialDesignDialog.react';
import PaymentActions from '../../../actions/PaymentActions';
import Poller from '../../../utils/Poller';
import {
    AccountPaymentAvailabilityFetcher,
    getAccountPaymentUnavailableMonthLabel,
    isCreditLineOffForCompany,
    isHeldByAccountPaymentAvailability,
    isInvoiceIdentityUnresolved,
} from '@/utils/AccountPaymentAvailability';
import ReservationManipulation from '../hotel/ReservationManipulation.react';
import Config from 'arbitrip-common/client/utils/Config';
import { addUtmDataToUrl } from 'arbitrip-common/client/utils/UtmUtils';
import DoubleBookingDialog from '../../general/DoubleBookingDialog';
import CouponCodeComponent from '@/components/pages/reservation-payment/components/CouponCodeComponent';
import PointsSliderComponent from '../results/deal/PointsSliderComponent';
import { isElalCompany, isIsracardCompany, isAirHaifaCompany } from '@/utils/isCompany';

const { PAYMENT_METHOD_TYPES, ELEMENT_IDS } = PaymentConstants;
const COUPON_DISABLED_COMPANY_IDS = ['699ddaa53a48e06b9f509cf3'];
const MARKETING_CONSENT_TRANSLATION_KEY = 'IsracardMarketingConsent';

// Single source of truth for "is the BNPL pay now / pay later card group the one being rendered".
// Both the reservation (bnpl_eligible) and the profile (credit_card_allowed) have to allow it.
function isBnplSelectionShown(reservation, profile) {
    return reservation?.bnpl_eligible === true && !!profile?.credit_card_allowed;
}

// Method to retrieve state from Stores.
// `live_account_payment_available` is the LIVE (per invoice-entity) half of the composed Account-payment
// answer — the half this module-level function cannot read, having no component instance. It defaults to
// `true` ("no opinion yet"), so a caller without a live answer behaves exactly as before OB-6.
function getPaymentComponentState(method, live_account_payment_available = true) {
    const private_travel = SearchStore.isPrivateTravel();
    const reservation = ReservationStore.getReservationData();
    const hotel = HotelStore.getHotelData();
    const profile = ProfileStore.getProfile();
    const post_pay = _.get(reservation, 'deal.post_pay');

    if (profile.freelance_agent) {
        if (method === PAYMENT_METHOD_TYPES.NONE || method === PAYMENT_METHOD_TYPES.CREDIT_CARD) {
            method = PAYMENT_METHOD_TYPES.CREDIT_CARD;
        } else if (method === PAYMENT_METHOD_TYPES.PAY_LATER) {
            method = PAYMENT_METHOD_TYPES.PAY_LATER;
        }
    } else if (isBnplSelectionShown(reservation, profile)) {
        // Same condition that makes getPaymentMethodsRender render the BNPL pay_now/pay_later radio group.
        // Once it applies, that group's own two values own `method`, so the private_travel / post_pay /
        // company_policy resolution below must not run at all — getPaymentComponentState is re-run by
        // _onChange on every ReservationStore/PaymentStore change event, and letting those branches run
        // would silently overwrite an explicit "Pay later" selection while the user fills in card details.
        // Default to Pay now for any value that isn't one of the group's two options (e.g. the initial NONE).
        if (![PAYMENT_METHOD_TYPES.CREDIT_CARD, PAYMENT_METHOD_TYPES.BNPL].includes(method)) {
            method = PAYMENT_METHOD_TYPES.CREDIT_CARD;
        }
    } else if (private_travel) {
        method = PAYMENT_METHOD_TYPES.PRIVATE_CARD;
    } else if (post_pay) {
        // TODO: profile.pay_at_hotel_allowed ?
        method = PAYMENT_METHOD_TYPES.PAY_AT_HOTEL;
    } else if (method === PAYMENT_METHOD_TYPES.NONE) {
        // OB-6: never DEFAULT to a method that is offered-but-disabled. `company_policy_available` is
        // live and per booking; `company_policy_allowed` is the static permission and is untouched.
        // BOTH halves of the composed answer must be consulted here: this branch re-runs on every store
        // emit, so consulting only the snapshot would silently re-select Account payment after the live
        // answer had already ruled it out, and the /pay gate would then refuse the booking.
        if (
            profile.company_policy_allowed &&
            live_account_payment_available &&
            isAccountPaymentAvailable(reservation)
        ) {
            method = PAYMENT_METHOD_TYPES.COMPANY_POLICY;
        } else if (profile.credit_card_allowed) {
            method = PAYMENT_METHOD_TYPES.CREDIT_CARD;
        }
    }
    return {
        hotel,
        reservationData: reservation,
        paymentData: PaymentStore.getPaymentData(),
        travelersStatuses: ReservationStore.getCreateTravelerStatuses(),
        private_travel,
        method,
        nonRefundableModalIsOpen: false,
        didBook: false,
        redeem_points_usage: '',
        payThreeDSConnectorHelper: PayThreeDSConnectorHelper,
    };
}

// Define main Controller View
const PaymentComponent = createClass({
    displayName: 'PaymentComponent',

    // Get initial state from stores
    getInitialState: function () {
        return {
            ...getPaymentComponentState(PAYMENT_METHOD_TYPES.NONE),
            reservation_manipulation: {
                is_fail: false,
                is_fail_reservation_during_j5: false,
                is_fail_j4_charge: false,
                increase_price: '',
                isDiclaimerApproval: false,
            },
            marketingConsent: false,
            showPaymentLinkExpiredDialog: false,
            // null until the live per-identity answer lands; until then Account payment keeps today's
            // behaviour (the reservation's company_policy_available) rather than flickering.
            account_payment_availability: null,
            // BUG 62 — is a credit-line availability read IN FLIGHT for the selected invoice entity?
            // Owned by the fetcher (onPending); this is its mirror in state.
            account_payment_availability_pending: false,
        };
    },

    // Add change listeners to stores
    componentDidMount: function () {
        ReservationStore.addChangeListener(this._onChange);
        PaymentStore.addChangeListener(this._onChange);

        this._account_availability_fetcher = new AccountPaymentAvailabilityFetcher(
            this._onAccountPaymentAvailability,
            {
                // BUG 62 — the BUG 55 gate, on the button THIS component renders. BUG 55 wired it only
                // into ReservationPaymentPage's out-of-policy Request button; checkout passed no
                // `onPending`, so "Complete Booking" stayed live for the whole read on every booking
                // this component renders — a travel manager booking an approved request included.
                onPending: this._onAccountPaymentAvailabilityPending,
            },
        );
        this._scheduleAccountPaymentAvailabilityFetch();

        const searchTerms = SearchStore.getSearchTerms();
        const destination = _.get(searchTerms, 'destination') || null;
        const deal = _.get(this.state, 'reservationData.deal');

        Analytics.actions.interactions.proceededToPayment(this.props.profile, destination, searchTerms, deal, {
            bnpl_eligible: this.isBnplEligible(),
            bnpl_shown: this.shouldShowBnplSelection(),
        });

        this.ensureBnplDefaultSelection();
    },

    // Remove change listeners from stores
    componentWillUnmount: function () {
        PaymentStore.removeChangeListener(this._onChange);
        ReservationStore.removeChangeListener(this._onChange);

        this._account_availability_fetcher.dispose();
    },

    // TODO: getPriceValueInPoints: function () {

    // },

    componentDidUpdate: function (prevProps, prevState) {
        // loyalty points
        const prev_user_chosen_points = _.get(prevProps, 'points.user_chosen_points');
        const user_chosen_points = _.get(this.props, 'points.user_chosen_points');
        if (prev_user_chosen_points !== user_chosen_points) {
            const arbitrip_points_to_currency_exchange_rate = _.get(
                this.props.profile,
                'loyalty.arbitrip_points_to_currency_exchange_rate',
                0,
            );
            const price_value_in_points =
                arbitrip_points_to_currency_exchange_rate > 0
                    ? _.get(this.props.reservation, 'deal.totalPrice', 0) // TODO: round to match price in currency
                    : 0;
            const should_clear =
                (prev_user_chosen_points === null && !user_chosen_points) ||
                (prevState.redeem_locked && !this.state.redeem_locked && !this.state.redeem_points_usage);
            const isNowFullyCovered = price_value_in_points > 0 && user_chosen_points >= price_value_in_points;

            this.setState({
                redeem_points_usage: should_clear ? '' : user_chosen_points,
                points_only: isNowFullyCovered,
            });
            this.deselectBnplIfFullyCovered();
        }

        // A coupon applied/removed after BNPL was selected can fully cover the price too — re-evaluate so Pay
        // Later doesn't stay selected on a zero balance.
        const prev_coupon = _.get(prevProps, 'couponValidation');
        const coupon = _.get(this.props, 'couponValidation');
        if (prev_coupon !== coupon) {
            this.deselectBnplIfFullyCovered();
        }

        //double booking
        const busy = ReservationConstants.STATUS.BUSY;
        if (
            prevProps.doubleBookingStatus === busy &&
            this.props.doubleBookingStatus !== busy &&
            !this.props.isDoubleBooking
        ) {
            this.advanceRefundableReservationOrOpenNonRefundableModal();
        }

        // OB-6 follow-up: the initial default method is resolved (see getPaymentComponentState) against
        // a payment-placeholder reservation, before the real company_policy_available payload has
        // landed — at that point it reads as undefined/unknown, which isAccountPaymentAvailable treats
        // as available. Once the real value arrives (undefined -> true/false), re-check: if Account was
        // defaulted-to and is now actually unavailable, correct it — otherwise the customer submits on
        // Account and gets refused at /pay.
        const prev_company_policy_available = _.get(prevState, 'reservationData.company_policy_available');
        const company_policy_available = _.get(this.state, 'reservationData.company_policy_available');
        if (prev_company_policy_available === undefined && company_policy_available !== undefined) {
            this.reevaluateDefaultPaymentMethod();
        }

        // The invoice identity is mutated ON the reservation object held by ReservationStore
        // (UPDATE_INVOICE_OPTION), so prevState.reservationData and this.state.reservationData can be the
        // very same reference — comparing prevState here would miss the switch entirely. Compare the
        // identity key we last fetched for instead.
        this._scheduleAccountPaymentAvailabilityFetch();

        this.ensureBnplDefaultSelection();
    },

    // getPaymentComponentState only hands `method` over to the BNPL pay now/pay later group once both the
    // reservation (bnpl_eligible) and the profile (credit_card_allowed) have settled, and this component does
    // not listen to ProfileStore — so `method` can still hold a value matching neither radio (e.g.
    // COMPANY_POLICY from the initial state) while the group is already rendered, leaving both cards
    // unchecked. Default it back to Pay now whenever that group is the one being shown.
    ensureBnplDefaultSelection() {
        if (!this.shouldShowBnplSelection()) {
            return;
        }
        if ([PAYMENT_METHOD_TYPES.CREDIT_CARD, PAYMENT_METHOD_TYPES.BNPL].includes(this.state.method)) {
            return;
        }
        this.setState({ method: PAYMENT_METHOD_TYPES.CREDIT_CARD });
        this.props.onPaymentMethodChange?.(PAYMENT_METHOD_TYPES.CREDIT_CARD);
    },

    // Identity of the question being asked: which booking, charged to which invoice entity. No
    // reservation id means it is still initializing and there is nothing to ask about. The fetcher
    // debounces, drops out-of-order answers, and fails open — see AccountPaymentAvailabilityFetcher.
    _scheduleAccountPaymentAvailabilityFetch() {
        this._account_availability_fetcher.schedule(
            _.get(this.state, 'reservationData.id'),
            _.get(this.state, 'reservationData.invoice_id'),
            {
                // BUG 51 — before the customer picks an invoice entity there is no cap to ask about,
                // and an answer about the resolver's positional default would WITHHOLD A PAYMENT
                // METHOD over a cap they never selected. Worse here than on the request button: the
                // option disappears, and reevaluateDefaultPaymentMethod moves the selection off
                // Account payment on the strength of it.
                identity_unresolved: isInvoiceIdentityUnresolved({
                    company: _.get(this.props, 'profile.company'),
                    private_travel: this.state.private_travel,
                    invoice_id: _.get(this.state, 'reservationData.invoice_id'),
                }),
                credit_line_off: isCreditLineOffForCompany(_.get(this.props, 'profile.company')),
            },
        );
    },

    // A null answer arrives when the invoice identity CHANGES: the previous entity's answer does not
    // describe the new one, so for the debounce + round-trip window it would name the wrong
    // bucket_month and keep Account payment disabled even when the new entity has room. Drop back to
    // "no opinion yet" so the snapshot decides, exactly as before the first answer landed — the
    // FAIL-OPEN direction.
    _onAccountPaymentAvailability(availability) {
        if (availability === null) {
            if (this.state.account_payment_availability !== null) {
                this.setState({ account_payment_availability: null });
            }

            return;
        }

        this.setState({ account_payment_availability: availability }, this.reevaluateDefaultPaymentMethod);
    },

    // BUG 62 — edge-triggered by the fetcher: `true` from the moment a read is scheduled for a newly
    // selected entity (debounce included), `false` when THAT read answers, whatever it answers. The
    // fetcher guarantees the pair, so this can never strand the button.
    _onAccountPaymentAvailabilityPending(pending) {
        this.setState({ account_payment_availability_pending: pending });
    },

    // PENDING FAILS CLOSED; a FAILED READ FAILS OPEN (the same split as the Request button's gate in
    // ReservationPaymentPage). The verdict about to land can withdraw Account payment, so a click
    // inside the window submits on a method the page is still deciding about — but ONLY Account
    // payment: every other method is never held (see isHeldByAccountPaymentAvailability).
    isHeldByAccountPaymentAvailability() {
        return isHeldByAccountPaymentAvailability({
            pending: this.state.account_payment_availability_pending,
            account_payment_selected: this.state.method === PAYMENT_METHOD_TYPES.COMPANY_POLICY,
        });
    },

    // The live per-identity answer COMPOSES with the one-shot `company_policy_available` snapshot rather
    // than replacing it: unavailable if EITHER says so. Before the live answer lands it is null, which
    // leaves today's behaviour untouched.
    isAccountPaymentOffered() {
        return isAccountPaymentAvailable(this.state.reservationData) && this.isLiveAccountPaymentAvailable();
    },

    // The live half on its own, for callers that read the snapshot themselves — `getPaymentComponentState`
    // re-reads the reservation straight from the store, so it must be given ONLY what it cannot see (and
    // must not be handed a snapshot older than the one it is about to read).
    isLiveAccountPaymentAvailable() {
        const live = this.state.account_payment_availability;

        return live ? live.available : true;
    },

    // ONE neutral string for every refusal cause. The endpoint's `reason` is for logs only and must
    // never be rendered (see @/utils/AccountPaymentAvailability) — the only thing that varies the copy is
    // whether we know which month is short.
    getAccountPaymentUnavailableReason() {
        const { t } = this.props;
        const month = getAccountPaymentUnavailableMonthLabel(this.state.account_payment_availability);

        if (!month) {
            return t('PaymentSection.AccountPaymentUnavailableNoMonth');
        }

        return t('PaymentSection.AccountPaymentUnavailableForMonth', { month });
    },

    // Corrects a stale COMPANY_POLICY default once real availability data shows it is not actually
    // available. Only ever moves the method AWAY from COMPANY_POLICY — never overrides an explicit
    // user selection of any other method.
    reevaluateDefaultPaymentMethod() {
        if (this.state.method !== PAYMENT_METHOD_TYPES.COMPANY_POLICY) {
            return;
        }

        if (this.isAccountPaymentOffered()) {
            return;
        }

        const method = this.props.profile.credit_card_allowed
            ? PAYMENT_METHOD_TYPES.CREDIT_CARD
            : PAYMENT_METHOD_TYPES.NONE;

        this.setState({ method });
        this.props.onPaymentMethodChange?.(method);
    },

    handlePaymentMethodChange(e) {
        const method = e.target.value;
        this.setState({ method });
        this.props.onPaymentMethodChange?.(method);

        // Only the BNPL radio group (renderBnplPaymentSelection) represents a pay_now/pay_later toggle.
        // The legacy getPaymentMethodsRender radio group shares this handler but is a payment-method
        // selector, not a BNPL toggle — gate on the exact condition that renders the BNPL selection.
        if (this.shouldShowBnplSelection()) {
            const { bnplAmounts } = this.getBnplDiscountInputs();
            const reservationData = this.state.reservationData;
            Analytics.actions.interactions.bnplOptionSelected({
                selected_option:
                    method === PAYMENT_METHOD_TYPES.BNPL
                        ? BNPL_SELECTED_OPTION.PAY_LATER
                        : BNPL_SELECTED_OPTION.PAY_NOW,
                pay_now_ils: bnplAmounts ? bnplAmounts.payNowIls : null,
                bnpl_amount:
                    reservationData?.bnpl_usd_amount_with_fee != null
                        ? Number(reservationData.bnpl_usd_amount_with_fee)
                        : null,
                bnpl_currency: BNPL_CURRENCY_USD,
                deferred_payment_fee_pct: reservationData?.bnpl_fee_percentage ?? null,
                company_id: _.get(this.props.profile, 'company.id'),
                checkout_flow_variant: CHECKOUT_FLOW_VARIANT.DESKTOP,
            });
        }
    },
    getPaymentDeadlineDate() {
        if (this.state?.reservationData?.pay_later_deadline) {
            return moment(this.state?.reservationData?.pay_later_deadline);
        }

        return false;
    },
    isBnplEligible() {
        return this.state?.reservationData?.bnpl_eligible === true;
    },
    shouldShowBnplSelection() {
        return isBnplSelectionShown(this.state?.reservationData, this.props.profile);
    },
    isBnplApplicable() {
        return !!this.state?.reservationData?.bnpl_charge_date;
    },
    // Single source of truth for the BNPL discount inputs (points + coupon) and the resulting amounts,
    // shared by the render and the auto-deselect logic so they can never drift. coupon_discount_value and
    // points_discount_ils are both already in the BNPL company's ILS deal currency (ElAl coupons are emitted
    // in ILS), matching bnpl_ils_amount — summed directly, no FX conversion.
    getBnplDiscountInputs() {
        const reservationData = this.state.reservationData;
        const user_chosen_points = _.get(this.props, 'points.user_chosen_points', 0);
        const points_exchange_rate =
            _.get(this.props, 'profile.loyalty.arbitrip_points_to_currency_exchange_rate') ||
            _.get(this.props, 'points.points_ex_rate', 0);
        const points_discount_ils =
            user_chosen_points > 0 && points_exchange_rate > 0 ? user_chosen_points * points_exchange_rate : 0;
        const coupon_validation = this.props.couponValidation;
        const coupon_discount_value =
            coupon_validation?.eligible && _.isFinite(coupon_validation?.discount?.amount)
                ? Math.max(0, coupon_validation.discount.amount || 0)
                : 0;
        const total_discount_ils = points_discount_ils + coupon_discount_value;
        const bnplAmounts = computeBnplAmounts({
            bnplIlsAmount: reservationData?.bnpl_ils_amount,
            bnplIlsRate: reservationData?.bnpl_ils_rate,
            bnplFeePercentage: reservationData?.bnpl_fee_percentage ?? 2,
            pointsDiscountIls: total_discount_ils,
            pointIlsValue: points_exchange_rate,
        });
        return { points_discount_ils, coupon_discount_value, points_exchange_rate, total_discount_ils, bnplAmounts };
    },
    // Switch a selected BNPL method back to credit card once discounts (points and/or coupon) fully cover the
    // price — Pay Later cannot apply a fee to a zero balance (AR-3305).
    deselectBnplIfFullyCovered() {
        const { bnplAmounts } = this.getBnplDiscountInputs();
        const isFullyCovered = bnplAmounts != null && bnplAmounts.isFullyCovered;
        if (isFullyCovered && this.state.method === PAYMENT_METHOD_TYPES.BNPL) {
            this.setState({ method: PAYMENT_METHOD_TYPES.CREDIT_CARD });
            this.props.onPaymentMethodChange?.(PAYMENT_METHOD_TYPES.CREDIT_CARD);
            return true;
        }
        return false;
    },
    renderBnplLockedPaymentSelection() {
        const { t } = this.props;
        const { reservationData } = this.state;
        const method = this.state.method;

        const bnpl_charge_date = reservationData?.bnpl_charge_date;
        const cancellation_date = _.get(reservationData, 'deal.dca.dealIsFullyRefundableTill');
        const { bnplAmounts } = this.getBnplDiscountInputs();

        const chargeDate = bnpl_charge_date ? moment(bnpl_charge_date).format('DD MMM YYYY') : '';
        const cancellationDateFormatted = cancellation_date
            ? moment.utc(cancellation_date).startOf('day').subtract(1, 'minute').format('DD MMM YYYY HH:mm')
            : '';

        const isPayLater = method === PAYMENT_METHOD_TYPES.BNPL;

        const payNowUsdFormatted =
            bnplAmounts && bnplAmounts.payNowUsd != null
                ? String(bnplAmounts.payNowUsd)
                : reservationData?.bnpl_usd_amount
                  ? parseFloat(reservationData.bnpl_usd_amount).toFixed(0)
                  : '';
        const usdAmountFormatted =
            bnplAmounts && bnplAmounts.payLaterUsd != null
                ? String(bnplAmounts.payLaterUsd)
                : reservationData?.bnpl_usd_amount_with_fee
                  ? parseFloat(reservationData.bnpl_usd_amount_with_fee).toFixed(0)
                  : '';
        const ilsWithFeeFormatted = bnplAmounts ? String(bnplAmounts.payLaterIls) : '';

        const amount = isPayLater ? usdAmountFormatted : payNowUsdFormatted;
        const title = isPayLater
            ? (chargeDate ? t('PaymentSection.PayLaterOn', { date: chargeDate }) : t('PaymentSection.PayLaterBnpl'))
            : t('PaymentSection.PayNow');

        return (
            <div className="bnpl-payment-selection">
                <h5 className="bnpl-payment-selection__title">{t('PaymentSection.WhenShouldWeCharge')}</h5>
                <div className="bnpl-locked-card">
                    <div className="bnpl-locked-card__top">
                        <div className="bnpl-locked-card__left">
                            <div className="bnpl-locked-card__title">{title}</div>
                            <div className="bnpl-locked-card__amount">
                                {amount && <div className="bnpl-option__price">${amount}</div>}
                                {isPayLater && chargeDate && (
                                    <div className="bnpl-option__description">
                                        <Trans
                                            i18nKey="PaymentSection.BnplPayLaterDescShort"
                                            values={{ date: chargeDate }}
                                            components={{ bold: <span className="bnpl-option__ils-highlight" /> }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="bnpl-locked-card__right">
                            {cancellationDateFormatted && (
                                <div className="bnpl-option__cancellation">
                                    <FreeCancelationIcon className="bnpl-option__cancellation-icon" />
                                    {t('PaymentSection.FullyRefundableUntil', { date: cancellationDateFormatted })}
                                </div>
                            )}
                            <div className="bnpl-option__points-redeemed">
                                <ElalCheckmarkIcon className="bnpl-option__points-redeemed-icon" />
                                {t('PaymentSection.BnplPointsRedeemed')}
                            </div>
                        </div>
                    </div>
                    {isPayLater && (
                        <>
                            <div className="bnpl-option__divider" />
                            <div className="bnpl-option__disclaimer">
                                <p>
                                    {ilsWithFeeFormatted ? (
                                        <Trans
                                            i18nKey="PaymentSection.BnplPayLaterDisclaimerFull"
                                            values={{ ilsAmount: ilsWithFeeFormatted }}
                                            components={{ bold: <span className="bnpl-option__ils-highlight" /> }}
                                        />
                                    ) : (
                                        t('PaymentSection.BnplDisclaimer')
                                    )}
                                </p>
                                <p>{t('PaymentSection.BnplFeeIncluded')}</p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    },

    renderBnplPaymentSelection() {
        if (this.props.shouldLockForm) {
            return this.renderBnplLockedPaymentSelection();
        }

        const { t } = this.props;
        const reservationData = this.state.reservationData;

        const bnpl_ils_amount = reservationData?.bnpl_ils_amount;
        const bnpl_usd_amount = reservationData?.bnpl_usd_amount;
        const bnpl_usd_amount_with_fee = reservationData?.bnpl_usd_amount_with_fee;
        const bnpl_charge_date = reservationData?.bnpl_charge_date;
        const cancellation_date = _.get(reservationData, 'deal.dca.dealIsFullyRefundableTill');

        const { points_discount_ils, points_exchange_rate, bnplAmounts } = this.getBnplDiscountInputs();

        const chargeDate = bnpl_charge_date ? moment(bnpl_charge_date).format('DD MMM YYYY') : '';
        const cancellationDateFormatted = cancellation_date ? moment.utc(cancellation_date).startOf('day').subtract(1, 'minute').format('DD MMM YYYY HH:mm') : '';
        const ilsAmountFormatted = bnplAmounts && !bnplAmounts.isFullyCovered ? String(bnplAmounts.payNowIls) : '';
        const ilsWithFeeFormatted = bnplAmounts ? String(bnplAmounts.payLaterIls) : '';
        const payNowUsdFormatted =
            bnplAmounts && bnplAmounts.payNowUsd != null
                ? String(bnplAmounts.payNowUsd)
                : bnpl_usd_amount
                  ? parseFloat(bnpl_usd_amount).toFixed(0)
                  : '';
        const usdAmountFormatted =
            bnplAmounts && bnplAmounts.payLaterUsd != null
                ? String(bnplAmounts.payLaterUsd)
                : bnpl_usd_amount_with_fee
                  ? parseFloat(bnpl_usd_amount_with_fee).toFixed(0)
                  : '';
        const isFullyCovered = bnplAmounts != null && bnplAmounts.isFullyCovered;
        // Distinguish the cause so the disabled-reason copy is accurate: points alone vs. coupon (or coupon + points).
        const coveredByPointsAlone =
            points_discount_ils > 0 &&
            (points_discount_ils >= bnpl_ils_amount ||
                (points_exchange_rate > 0 &&
                    Math.max(0, bnpl_ils_amount - points_discount_ils) < points_exchange_rate));
        const fullyCoveredLabel = coveredByPointsAlone
            ? t('PaymentSection.BnplFullyCoveredByPoints')
            : t('PaymentSection.BnplFullyCovered');

        return (
            <div className="bnpl-payment-selection">
                <h5 className="bnpl-payment-selection__title">{t('PaymentSection.WhenShouldWeCharge')}</h5>
                <RadioGroup
                    value={this.state.method}
                    onChange={this.handlePaymentMethodChange}
                    data-testid="bnpl-payment-selection"
                >
                    <div className={`bnpl-option${this.state.method === PAYMENT_METHOD_TYPES.CREDIT_CARD ? ' bnpl-option--selected' : ''}`}>
                        <div className="bnpl-option__content-row">
                            <FormControlLabel
                                className="bnpl-option__selector"
                                value={PAYMENT_METHOD_TYPES.CREDIT_CARD}
                                control={<Radio inputProps={{ 'data-testid': 'bnpl-pay-now' }} />}
                                label={
                                    <span className="bnpl-option__left">
                                        <span className="bnpl-option__label">{t('PaymentSection.PayNow')}</span>
                                        <span className="bnpl-option__amount">
                                            {payNowUsdFormatted && <span className="bnpl-option__price">${payNowUsdFormatted}</span>}
                                            {ilsAmountFormatted && (
                                                <span className="bnpl-option__description">
                                                    <Trans
                                                        i18nKey="PaymentSection.BnplPayNowDescShort"
                                                        values={{ amount: ilsAmountFormatted }}
                                                        components={{ bold: <span className="bnpl-option__ils-highlight" /> }}
                                                    />
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                }
                                disabled={this.props.shouldLockForm}
                            />
                            <div className="bnpl-option__right">
                                {cancellationDateFormatted && (
                                    <div className="bnpl-option__cancellation">
                                        <FreeCancelationIcon className="bnpl-option__cancellation-icon" />
                                        {t('PaymentSection.FullyRefundableUntil', { date: cancellationDateFormatted })}
                                    </div>
                                )}
                                <div className="bnpl-option__points-redeemed">
                                    <ElalCheckmarkIcon className="bnpl-option__points-redeemed-icon" />
                                    {t('PaymentSection.BnplPointsRedeemed')}
                                </div>
                            </div>
                        </div>
                        {!isFullyCovered && (
                            <>
                                <div className="bnpl-option__divider" />
                                <div className="bnpl-option__badge">
                                    <span className="bnpl-option__badge-text">{t('PaymentSection.BnplInstalments')}</span>
                                    <BnplInstalmentsIcon className="bnpl-option__badge-icon" />
                                </div>
                            </>
                        )}
                    </div>
                    <div className={`bnpl-option${this.state.method === PAYMENT_METHOD_TYPES.BNPL ? ' bnpl-option--selected' : ''}${isFullyCovered ? ' bnpl-option--disabled' : ''}`}>
                        <div className="bnpl-option__content-row">
                            <FormControlLabel
                                className="bnpl-option__selector"
                                value={PAYMENT_METHOD_TYPES.BNPL}
                                control={<Radio inputProps={{ 'data-testid': 'bnpl-pay-later' }} />}
                                label={
                                    <span className="bnpl-option__left">
                                        <span className="bnpl-option__label">{chargeDate ? t('PaymentSection.PayLaterOn', { date: chargeDate }) : t('PaymentSection.PayLaterBnpl')}</span>
                                        <span className="bnpl-option__amount">
                                            {usdAmountFormatted && <span className="bnpl-option__price">${usdAmountFormatted}</span>}
                                            {!isFullyCovered && chargeDate && (
                                                <span className="bnpl-option__description">
                                                    <Trans
                                                        i18nKey="PaymentSection.BnplPayLaterDescShort"
                                                        values={{ date: chargeDate }}
                                                        components={{ bold: <span className="bnpl-option__ils-highlight" /> }}
                                                    />
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                }
                                disabled={this.props.shouldLockForm || isFullyCovered}
                            />
                            <div className="bnpl-option__right">
                                {isFullyCovered ? (
                                    <p className="bnpl-option__not-available">{fullyCoveredLabel}</p>
                                ) : (
                                    <>
                                        {cancellationDateFormatted && (
                                            <div className="bnpl-option__cancellation">
                                                <FreeCancelationIcon className="bnpl-option__cancellation-icon" />
                                                {t('PaymentSection.FullyRefundableUntil', { date: cancellationDateFormatted })}
                                            </div>
                                        )}
                                        <div className="bnpl-option__points-redeemed">
                                            <ElalCheckmarkIcon className="bnpl-option__points-redeemed-icon" />
                                            {t('PaymentSection.BnplPointsRedeemed')}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {!isFullyCovered && (
                            <>
                                <div className="bnpl-option__divider" />
                                <div className="bnpl-option__disclaimer">
                                    <p>
                                        {ilsWithFeeFormatted ? (
                                            <Trans
                                                i18nKey="PaymentSection.BnplPayLaterDisclaimerFull"
                                                values={{ ilsAmount: ilsWithFeeFormatted }}
                                                components={{ bold: <span className="bnpl-option__ils-highlight" /> }}
                                            />
                                        ) : (
                                            t('PaymentSection.BnplDisclaimer')
                                        )}
                                    </p>
                                    <p>{t('PaymentSection.BnplFeeIncluded')}</p>
                                </div>
                            </>
                        )}
                    </div>
                </RadioGroup>
            </div>
        );
    },
    getPaymentMethodsRender() {
        if (this.shouldShowBnplSelection()) {
            return this.renderBnplPaymentSelection();
        }

        const { t } = this.props;
        let payment_methods = [];

        if (this.props.profile.freelance_agent) {
            payment_methods.push({ method: PAYMENT_METHOD_TYPES.CREDIT_CARD, label: t('PaymentSection.CreditCard') });
            if (this.getPaymentDeadlineDate()) {
                payment_methods.push({
                    method: PAYMENT_METHOD_TYPES.PAY_LATER,
                    label: t('PaymentSection.BookNowPayLater'),
                });
            }
        } else if (!this.state.private_travel) {
            if (this.state.method === PAYMENT_METHOD_TYPES.PAY_AT_HOTEL) {
                payment_methods.push({
                    method: PAYMENT_METHOD_TYPES.PAY_AT_HOTEL,
                    label: t('PaymentSection.PayAtHotel'),
                });
            } else if (this.props.profile.credit_card_allowed && this.props.profile.company_policy_allowed) {
                payment_methods.push({
                    method: PAYMENT_METHOD_TYPES.CREDIT_CARD,
                    label: t('PaymentSection.CreditCard'),
                });
                // OB-6: rendered DISABLED WITH A REASON rather than removed. An unexplained missing
                // payment method generates support tickets and looks like a bug; this follows the BNPL
                // precedent below, which is the closest customer-facing pattern in this component.
                const account_available = this.isAccountPaymentOffered();
                payment_methods.push({
                    method: PAYMENT_METHOD_TYPES.COMPANY_POLICY,
                    label: t('PaymentSection.AccountPayment'),
                    disabled: !account_available,
                    disabled_reason: account_available ? undefined : this.getAccountPaymentUnavailableReason(),
                });
            } else if (this.props.profile.credit_card_allowed) {
                payment_methods.push({
                    method: PAYMENT_METHOD_TYPES.CREDIT_CARD,
                    label: t('PaymentSection.CreditCard'),
                });
            } else if (this.shouldShowAccountPaymentUnavailableNotice()) {
                // OB-6: an account-payment-only company has nothing to choose between, so no selector is
                // rendered for it while Account payment works. When it is refused, that silence leaves the
                // customer with no method and no explanation — so the disabled entry is rendered purely to
                // carry the reason.
                payment_methods.push({
                    method: PAYMENT_METHOD_TYPES.COMPANY_POLICY,
                    label: t('PaymentSection.AccountPayment'),
                    disabled: true,
                    disabled_reason: this.getAccountPaymentUnavailableReason(),
                });
            }
        } else if (this.props.loyaltyEnabled) {
            payment_methods.push({ method: PAYMENT_METHOD_TYPES.PRIVATE_CARD, label: t('PaymentSection.CreditCard') });
        }

        if (this.props.profile.credit_card_allowed && this.isBnplApplicable()) {
            payment_methods.push({
                method: PAYMENT_METHOD_TYPES.BNPL,
                label: t('PaymentSection.PayLaterBnpl'),
                disabled: true,
                disabled_reason: t('PaymentSection.BnplDeadlineTooClose'),
            });
        }

        if (payment_methods.length > 0) {
            return (
                <FormControl>
                    {/* <FormLabel id="payment-method-radio-buttons-group">{t('PaymentSection.Method')}</FormLabel> */}
                    <RadioGroup
                        aria-labelledby="payment-method-radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={this.state.method}
                        onChange={this.handlePaymentMethodChange}
                        data-testid="payments-radio-group"
                    >
                        {payment_methods.map((pm) => (
                            <Box key={pm.method}>
                                <FormControlLabel
                                    value={pm.method}
                                    control={<Radio inputProps={{ 'data-testid': `payment-option-${pm.method}` }} />}
                                    label={pm.label}
                                    disabled={pm.disabled || this.props.shouldLockForm}
                                />
                                {pm.disabled_reason && (
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', ml: 4, mt: -0.5, color: 'text.secondary' }}
                                    >
                                        {pm.disabled_reason}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        }

        return null;
    },
    getBookNowPayLaterDisclaimer() {
        const { t } = this.props;
        if (this.state.method === PAYMENT_METHOD_TYPES.PAY_LATER) {
            const payment_deadline_date = this.getPaymentDeadlineDate();
            return (
                <div className="book-now-pay-later-disclaimer">
                    <div>
                        {t('PaymentDeadline')}{' '}
                        <span className="payment-deadline-date">{payment_deadline_date.format('D MMM YYYY')}</span>,
                    </div>
                    <div>{t('BookingWillBeCanceled')}</div>
                </div>
            );
        }

        return null;
    },

    async advanceReservation() {
        const {
            reservationData: reservation,
            trip_id,
            use_bank_money,
            search_token,
            send_voucher_separately_from_confirmation_mail,
            send_email: send_email_confirmation,
            combtas_id,
            travel_booster_docket_number,
            travel_booster_should_create_a_new_docket,
            price_comparable,
            currency,
            client_custom_fields,
            search_terms,
            hotel_search_token,

            show_points,
            user_chosen_points,
        } = this.props.advanceReservationFields;

        const coupon_code = _.get(this.props, 'couponValidation.code');
        const coupon_eligible = _.get(this.props, 'couponValidation.eligible');

        // adding contact details(Main guest data)
        if (this.state.private_travel) {
            const { first_name = '', last_name = '', email = '', phone = '' } = reservation.travelers[0] || {};

            reservation.contact_details = {
                first_name,
                last_name,
                email,
                tel: phone,
            };
        }

        const hotel = this.state.hotel;
        const payment_type = this.state.method;
        const hotel_recommended_index = this.state.reservationData.hotel.recommended_index;
        const deal_index = this.state.reservationData.deal.deal_index;
        const charge_currency = this.props.profile.charge_currency;

        const reservation_manipulation = this.state.reservation_manipulation;
        const isTester = Config.dev_mode || this.props.profile?.is_arbi_tester;

        const params = {
            hotel,
            payment_type,
            reservation,
            trip_id,
            use_bank_money,
            search_token,
            send_email_confirmation,
            combtas_id,
            travel_booster_docket_number,
            travel_booster_should_create_a_new_docket,
            price_comparable,
            currency,
            client_custom_fields,
            search_terms,
            hotel_recommended_index,
            deal_index,
            hotel_search_token,
            charge_currency,

            show_points,
            user_chosen_points,
            marketing_consent: this.state.marketingConsent === true,
            ...(coupon_code && coupon_eligible ? { coupon_code } : {}),
            coupon_validation: this.props.couponValidation,
            send_voucher_separately_from_confirmation_mail,
            ...(isTester ? { reservation_manipulation } : {}),
        };

        if (this.props.profile?.company?.partners_ui?.use_expedia_payment) {
            try {
                params.threeDSData = await this.state.payThreeDSConnectorHelper.initSession(
                    this.state.reservationData.deal,
                );
            } catch (err) {
                console.log('should be visible to user error', err);
            }
        }

        ReservationActions.advanceReservation(this.props.profile, params);
    },

    paymentClicked() {
        // A click that beats the re-render must not get past the gate the disabled prop draws.
        if (this.isHeldByAccountPaymentAvailability()) {
            return;
        }

        if ( ( isIsracardCompany() || isElalCompany() || isAirHaifaCompany() )  && !this.state.disclaimerApproval ){
            this.setState({ invalidDisclaimerApproval: true })
            return;
        }
        const { validateTravelersContacts, forceTravelBoosterDocketNumberValidation, clearSpecialRequests } =
            this.props;

        if (_.isFunction(forceTravelBoosterDocketNumberValidation)) {
            forceTravelBoosterDocketNumberValidation();
        }

        if (_.isFunction(validateTravelersContacts)) {
            validateTravelersContacts()
                .then(() => {
                    this.checkFormValidation();
                })
                .catch((e) => {
                    console.log(e);
                });
        }

        if (_.isFunction(clearSpecialRequests)) {
            clearSpecialRequests();
        }
    },

    checkFormValidation() {
        let { validForm, openValidationModal, profile, checkDoubleBooking } = this.props;
        const enableDoubleBookingCheck = _.get(profile, 'company.settings.enable_double_booking_check', true);

        if (validForm) {
            if (enableDoubleBookingCheck) {
                checkDoubleBooking();
            } else {
                this.advanceRefundableReservationOrOpenNonRefundableModal();
            }
        } else {
            if (_.isFunction(openValidationModal)) {
                openValidationModal();
            }
        }
    },

    renderDoubleBookingDialog() {
        return (
            <DoubleBookingDialog
                isOpen={this.props.isDoubleBookingModalIsOpen}
                responseAction={this.onDialogResponse}
                newReservationDetails={this.props.newReservationDetails}
                existingReservationsDetails={this.props.existingReservationsDetails}
            />
        );
    },

    onDialogResponse(response) {
        this.props.handleDoubleBookingDialogResponse(response);
        if (response) {
            this.advanceRefundableReservationOrOpenNonRefundableModal();
        }
    },

    advanceRefundableReservationOrOpenNonRefundableModal() {
        const searchTerms = SearchStore.getSearchTerms();
        const destination = _.get(searchTerms, 'destination') || null;
        const deal = _.get(this.state, 'reservationData.deal');
        Analytics.actions.interactions.proceededToPaymentOpenPaymentForm(
            this.props.profile,
            destination,
            searchTerms,
            deal,
            this.props.hotel,
        );

        // if (!this.state.reservationData.deal.refundable) {
        // if (_.get(this.state.reservationData, 'deal.chooseprod.dca.nonRefundable') === true) {
        if (_.get(this.state.reservationData, 'deal.dca.nonRefundable') === true) {
            this.setState({ nonRefundableModalIsOpen: true });
        } else {
            this.advanceReservation();
        }
    },

    onRedeemPoints(event) {
        this.setState({ redeem_points: event.target.checked });
        PointsActions.saveChosenUserPoints('');
    },

    handleRedeemPointsUsageChange(event) {
        const points_input = event.target.value.replaceAll(/\D/g, '');
        this.setState({
            redeem_points_usage: points_input === '' ? '' : parseInt(points_input, 10),
            invalid_points_input_error_message:
                this.state.redeem_points_usage === points_input ? this.state.invalid_points_input_error_message : '',
        });
    },
    handleDisclaimerChange(event){
        this.setState({disclaimerApproval:event.target.checked});
    },
    handleMarketingConsentChange(event) {
        this.setState({ marketingConsent: event.target.checked });
    },
    handleAddClick(event) {
        const points_input = this.state.redeem_points_usage;
        const arbitrip_points_to_currency_exchange_rate = _.get(
            this.props.profile,
            'loyalty.arbitrip_points_to_currency_exchange_rate',
            0,
        );
        const price_value_in_points =
            arbitrip_points_to_currency_exchange_rate > 0
                ? _.get(this.props.reservation, 'deal.totalPrice', 0) / arbitrip_points_to_currency_exchange_rate // TODO: round to match price in currency
                : 0;
        const balance = _.get(this.props.points, 'balance', 0);

        // if (!_.isNumber(points_input)) {
        //     this.setState({ invalid_points_input_error_message: 'Please provide a number' });
        // } else if (points_input < 0) {
        //     this.setState({ invalid_points_input_error_message: 'Please provide a positive number' });
        // } else if (points_input.indexOf('.') >= 0) {
        //     this.setState({ invalid_points_input_error_message: 'Please provide an integer' });
        // } else

        // TODO: move to initial state
        const limits = [
            { value: price_value_in_points, reason: 'price value' },
            { value: balance, reason: 'your balance' },
        ].sort((a, b) => a.value - b.value);

        if (points_input > limits[0].value) {
            this.setState({
                invalid_points_input_error_message: `Please provide a value up to ${limits[0].value.toLocaleString()} (${limits[0].reason})`,
            });
        } else if (points_input > limits[1].value) {
            this.setState({
                invalid_points_input_error_message: `Please provide a value up to ${limits[1].value.toLocaleString()} (${limits[1].reason})`,
            });
        } else {
            this.setState({ redeem_busy: true });

            if (this.state.redeem_locked) {
                PointsActions.saveChosenUserPoints('');
                this.setState({
                    redeem_locked: false,
                    redeem_points_usage: '',
                });
            } else {
                PointsActions.saveChosenUserPoints(this.state.redeem_points_usage);
                this.setState({ redeem_locked: true });
            }

            const _self = this;
            setTimeout(() => {
                _self.setState({ redeem_busy: false });
            }, 1000);
        }
    },

    // `considerAvailability` gates whether the real-time (per-booking) `company_policy_available` check
    // participates. Structural callers (deciding whether the selector renders AT ALL) must pass false:
    // Account being temporarily unavailable does not change how many methods are OFFERED — it is still
    // rendered, shown-disabled-with-caption (see getPaymentMethodsRender) — so it must not make the
    // whole selector container disappear along with that caption (OB-6 follow-up).
    hasMultiplePaymentMethods(considerAvailability = true) {
        const company_policy_offered =
            _.get(this.props.profile, 'company_policy_allowed', false) &&
            (!considerAvailability || this.isAccountPaymentOffered());

        return (
            !this.state.private_travel &&
            this.state.method !== PAYMENT_METHOD_TYPES.PAY_AT_HOTEL &&
            _.get(this.props.profile, 'credit_card_allowed', false) &&
            (company_policy_offered || _.get(this.props.profile, 'pay_later_allowed', false))
        );
    },

    // OB-6: an account-payment-only company (`company_policy_allowed` without `credit_card_allowed`) falls
    // outside hasMultiplePaymentMethods entirely — that predicate requires credit_card_allowed — so the
    // selector container is gated on this too, or the disabled Account-payment entry would be built and
    // then never rendered. Deliberately narrow: it is true ONLY while there is a refusal to explain.
    shouldShowAccountPaymentUnavailableNotice() {
        return (
            !this.state.private_travel &&
            !this.props.profile.freelance_agent &&
            this.state.method !== PAYMENT_METHOD_TYPES.PAY_AT_HOTEL &&
            !_.get(this.props.profile, 'credit_card_allowed', false) &&
            _.get(this.props.profile, 'company_policy_allowed', false) &&
            !this.isAccountPaymentOffered()
        );
    },

    renderRedeemPoints() {
        if (isElalCompany()) {
            return null;
        }

        const { t } = this.props;
        const label = t('PaymentSection.RedeemPoints');
        const checkbox = (
            <Checkbox
                label={label}
                checked={this.state.redeem_points}
                onChange={this.onRedeemPoints}
                disabled={this.props.shouldLockForm || this.state.redeem_busy || this.state.redeem_locked}
                inputProps={{ 'data-testid': 'redeem-points-checkbox' }}
            />
        );

        return this.props.loyaltyEnabled ? (
            <React.Fragment>
                {this.hasMultiplePaymentMethods() && <hr />}
                <FormControlLabel control={checkbox} label={label} style={{ marginBottom: 0 }} />
                <Collapse in={this.state.redeem_points}>
                    <NewRedeemPoints
                        profile={this.props.profile}
                        points={this.props.points}
                        redeemPoints={this.state.redeem_points}
                        onRedeemPoints={this.onRedeemPoints}
                        shouldLockForm={this.props.shouldLockForm}
                        redeemBusy={this.state.redeem_busy}
                        redeemLocked={this.state.redeem_locked}
                        reservation={this.props.reservation}
                        privateTravel={this.props.privateTravel}
                        redeemPointsUsage={this.state.redeem_points_usage}
                        handleRedeemPointsUsageChange={this.handleRedeemPointsUsageChange}
                        invalidPointsInputErrorMessage={this.state.invalid_points_input_error_message}
                        handleAddClick={this.handleAddClick}
                    />
                </Collapse>
            </React.Fragment>
        ) : null;
    },

    getPaymentActionText() {
        const { t } = this.props;
        if ( this.state.points_only || isIsracardCompany() ) {
            return t('PaymentSection.CompleteBooking');
        }

        switch (this.state.method) {
            case PAYMENT_METHOD_TYPES.PRIVATE_CARD:
            case PAYMENT_METHOD_TYPES.CREDIT_CARD:
                return this.isBnplEligible() ? t('PaymentSection.PayNow') : t('ProceedToPayment');
            case PAYMENT_METHOD_TYPES.BNPL:
                return t('PaymentSection.SecureMyBooking');
            case PAYMENT_METHOD_TYPES.COMPANY_POLICY:
            case PAYMENT_METHOD_TYPES.PAY_AT_HOTEL:
            case PAYMENT_METHOD_TYPES.PAY_LATER:
                return t('PaymentSection.CompleteBooking');
            default: // Do nothing
        }

        return '';
    },
    renderPaymentActionsDialog(paymentActionText) {
        if (paymentActionText) {
            const { t } = this.props;
            const PAYMENT_ACTIONS = [
                <Button
                    key="cancel"
                    variant="text"
                    color="primary"
                    onClick={() => this.setState({ nonRefundableModalIsOpen: false })}
                >
                    {t('Cancel')}
                </Button>,
                <Button key="advance" variant="text" color="primary" onClick={this.advanceReservation}>
                    {paymentActionText}
                </Button>,
            ];
            return (
                <Dialog open={this.state.nonRefundableModalIsOpen}>
                    <DialogTitle>{t('NonRefundableSpaced')}</DialogTitle>
                    <DialogContent>
                        <>
                            {t('ThisReservationIs')} <b>{t('NonRefundableSpaced')}</b>,{' '}
                            {t('CancellationOrChangeWillIncurCosts')}.
                        </>
                    </DialogContent>
                    <DialogActions>{PAYMENT_ACTIONS}</DialogActions>
                </Dialog>
            );
        }

        return null;
    },

    renderPaymentMethod() {
        const { t } = this.props;
        const isExpediaPayment = this.props.profile?.company?.partners_ui?.use_expedia_payment;
        switch (this.state.method) {
            case PAYMENT_METHOD_TYPES.PRIVATE_CARD:
                if (isExpediaPayment) {
                    return (
                        <ExpediaCreditCardComponent
                            payThreeDSConnectorHelper={this.state.payThreeDSConnectorHelper}
                            couponValidation={this.props.couponValidation}
                        />
                    );
                }
                return (
                    <CreditCardComponent
                        mode="private"
                        advanceReservation={this.advanceReservation}
                        onPaymentLinkExpired={() => {
                        Poller.stopPaymentStatusPolling();
                        this.setState({ showPaymentLinkExpiredDialog: true });
                    }}
                        profile={this.props.profile}
                        couponValidation={this.props.couponValidation}
                    />
                );
            case PAYMENT_METHOD_TYPES.CREDIT_CARD:
                if (isExpediaPayment) {
                    return (
                        <ExpediaCreditCardComponent
                            payThreeDSConnectorHelper={this.state.payThreeDSConnectorHelper}
                            couponValidation={this.props.couponValidation}
                        />
                    );
                }
                return (
                    <CreditCardComponent
                        advanceReservation={this.advanceReservation}
                        onPaymentLinkExpired={() => {
                        Poller.stopPaymentStatusPolling();
                        this.setState({ showPaymentLinkExpiredDialog: true });
                    }}
                        profile={this.props.profile}
                        couponValidation={this.props.couponValidation}
                    />
                );
            case PAYMENT_METHOD_TYPES.BNPL:
                if (isExpediaPayment) {
                    return (
                        <ExpediaCreditCardComponent
                            payThreeDSConnectorHelper={this.state.payThreeDSConnectorHelper}
                            couponValidation={this.props.couponValidation}
                        />
                    );
                }
                return (
                    <CreditCardComponent
                        isBnpl={true}
                        advanceReservation={this.advanceReservation}
                        onPaymentLinkExpired={() => {
                            Poller.stopPaymentStatusPolling();
                            this.setState({ showPaymentLinkExpiredDialog: true });
                        }}
                        profile={this.props.profile}
                        couponValidation={this.props.couponValidation}
                    />
                );
            case PAYMENT_METHOD_TYPES.COMPANY_POLICY:
                return <CompanyPolicyComponent valid />;
            case PAYMENT_METHOD_TYPES.PAY_AT_HOTEL:
                return <PayAtHotelComponent valid />;
            case PAYMENT_METHOD_TYPES.PAY_LATER:
                return <CompanyPolicyComponent valid />;
            default: // Do nothing
        }

        return (
            <div className="payment-method">
                <div className="no-payment-message">
                    {t('ContactUsToFinalizeReservation')}{' '}
                    <a href={`mailto:${t('HelpEmail')}`} target="_blank" rel="noreferrer">
                        {t('ContactUs')}
                    </a>
                </div>
            </div>
        );
    },

    handleChargeCurrencyChange(e) {
        const currency = e.target.value;
        ProfileActions.changeChargeCurrency(currency);
    },

    getCurrencyExchangeRate(currency, initializing) {
        const precision = 2;
        const { reservationData } = this.state;
        const exchange_rates = _.get(reservationData, 'leisure_allowed_payment_currencies_exchange_rates', {});
        const exchange_rate = exchange_rates[currency];
        if (!exchange_rate && !initializing) {
            return `${currency}`; // fallback
        }

        const sum = exchange_rate ? (
            Currencies.getPriceWithDisplayCurrencyByPrecision(
                this.state.reservationData.deal.totalPrice,
                exchange_rate,
                currency,
                precision,
            )
        ) : (
            <CircularProgress size={13} />
        );

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <div>{currency}</div>
                <div
                    style={{
                        marginRight: 23,
                    }}
                >
                    {sum}
                </div>
            </div>
        );
    },

    changeChargeCurrencyRender() {
        const { t } = this.props;
        const component_width = 200;
        const initializing = _.get(this.state.reservationData, 'status') === ReservationConstants.STATUS.INITIALIZING;
        return this.props.canChangeChargeCurrency ? (
            <div className="charge-currency-container">
                <label htmlFor="charge_currency">{t('ChooseCreditChargeCurrency')}</label>
                <div className="select-container">
                    <Select
                        id="charge-currency"
                        value={this.props.profile.charge_currency}
                        renderValue={() =>
                            this.getCurrencyExchangeRate(this.props.profile.charge_currency, initializing)
                        }
                        onChange={this.handleChargeCurrencyChange}
                        disabled={this.props.shouldLockForm || initializing}
                        style={{
                            background: '#eee',
                            border: '1px solid #888',
                            borderRadius: 4,
                            width: component_width,
                            height: 37,
                            // TODO: blue borderColor when opened
                        }}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                            style: {
                                marginTop: 3,
                                marginLeft: -1,
                            },
                        }}
                    >
                        {this.props.profile.leisure_allowed_payment_currencies.map((cur) => (
                            <MenuItem value={cur} key={cur} style={{ width: component_width }}>
                                {this.getCurrencyExchangeRate(cur, initializing)}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            </div>
        ) : null;
    },

    getCountryName(country_code) {
        let country_name = '';
        if (country_code) {
            const regionNamesInEnglish = new Intl.DisplayNames(['en'], { type: 'region' });
            try {
                country_name = regionNamesInEnglish.of(country_code);
            } catch (e) {
                console.error(`Can not parse country value of ${country_code}`);
            }
        }
        return country_name;
    },

    renderDisclaimer() {
        const { t, reservation, shouldLockForm } = this.props;
        const { disclaimerApproval, invalidDisclaimerApproval, paymentData, method } = this.state;

        const isElal = isElalCompany();
        const isIsracard = isIsracardCompany();
        const isAirHaifa = isAirHaifaCompany();
        const isCardPayment = !paymentData.creditCardUrl && [PAYMENT_METHOD_TYPES.PRIVATE_CARD, PAYMENT_METHOD_TYPES.CREDIT_CARD, PAYMENT_METHOD_TYPES.BNPL].includes(method);
        const countryName = this.getCountryName(reservation.payment_country_code);

        const form_locked = shouldLockForm || (reservation?.status === ReservationConstants.STATUS.INITIALIZING);    
        
        const Link = ({ 
            className = "terms-of-supplier-link", 
            target = "_blank", 
            rel = "noreferrer", 
            ...rest 
        }) => <a className={className} target={target} rel={rel} {...rest} />;
        
        const sharedTransComponents = {
            termsLink: <Link href={addUtmDataToUrl(t('TermsAndConditionsUrl'), isIsracard)} />,
            privacyLink: <Link href={addUtmDataToUrl(t('PrivacyPolicyUrl'), isIsracard)} />,
            matmidLink: <Link href={t('ElalMatmidTermsUrl')} />,
            br: <br />
        };

        const disclaimerConfigs = [
            { condition: isElal, i18nKey: "PaymentSection.ElalDisclaimerText" },
            { condition: isIsracard, i18nKey: "PaymentSection.IsracardDisclaimerText" },
            { condition: isCardPayment, i18nKey: "PaymentSection.ByProceedingReservation" },
            { condition: true, i18nKey: "PaymentSection.ByCompletingReservation" }
        ];

        const activeDisclaimer = disclaimerConfigs.find(config => config.condition);

        return (
            <div className="disclaimer">
                { (isElal || isIsracard || isAirHaifa) && (
                    <div>
                        <Checkbox
                            className={invalidDisclaimerApproval ? 'error' : ''}
                            disabled={form_locked}
                            checked={disclaimerApproval}
                            onChange={this.handleDisclaimerChange}
                            inputProps={{ 'data-testid': isElal ? 'elal-disclaimer-checkbox' : isIsracard ? 'isracard-disclaimer-checkbox' : 'disclaimer-checkbox' }}
                        />
                    </div>
                )}
                <div>
                    {<Trans 
                        i18nKey={activeDisclaimer.i18nKey} 
                        components={sharedTransComponents} 
                    />}
                    {countryName && (<p>{t('PaymentSection.ProcessingCountry', { country: countryName })}</p>)}
                </div>
            </div>
        );
    },
    renderBnplPaymentNotice() {
        const { t, i18n } = this.props;
        const { reservationData } = this.state;

        if (this.state.method !== PAYMENT_METHOD_TYPES.BNPL) return null;

        const bnpl_charge_date = reservationData?.bnpl_charge_date;
        if (!bnpl_charge_date) return null;

        const chargeDate = moment(bnpl_charge_date).format('DD MMM YYYY');

        return (
            <div className={`payment-banner ${i18n.dir()}`}>
                <div className="banner-tooltip-icon"><InfoIcon /></div>
                <div className="banner-content">
                    <div className="banner-title">{t('PaymentSection.BnplNoticeTitle')}</div>
                    <div className="banner-body">
                        <Trans
                            i18nKey="PaymentSection.BnplNoticeBody"
                            values={{ date: chargeDate }}
                            components={{ bold: <strong /> }}
                        />
                    </div>
                </div>
            </div>
        );
    },
    renderMarketingConsent() {
        const { t, shouldLockForm, reservation } = this.props;
        const { marketingConsent } = this.state;

        if (!isIsracardCompany()) {
            return null;
        }

        const form_locked = shouldLockForm || (reservation?.status === ReservationConstants.STATUS.INITIALIZING);

        return (
            <div className="marketing-consent">
                <div>
                    <Checkbox
                        disabled={form_locked}
                        checked={marketingConsent}
                        onChange={this.handleMarketingConsentChange}
                    />
                </div>
                <div className="marketing-consent-text">{t(MARKETING_CONSENT_TRANSLATION_KEY)}</div>
            </div>
        );
    },
    renderPaymentAction(paymentActionText) {
        const { busy, madeReservation, checkingDoubleBooking } = this.props;
        const { reservationData } = this.state;

        const initializing = reservationData?.status === ReservationConstants.STATUS.INITIALIZING;

        if (paymentActionText && !this.state.paymentData.creditCardUrl) {
            const spinner = (busy || checkingDoubleBooking) && <i className="fa fa-spin fa-spinner fa-2x" />;
            const availability_pending = this.isHeldByAccountPaymentAvailability();

            return (
                <div className="payment-action">
                    <input
                        role="button"
                        type="button"
                        id={ELEMENT_IDS.PAYMENT_ACTION_BUTTON}
                        className="btn btn-primary flat"
                        onClick={this.paymentClicked}
                        value={paymentActionText}
                        disabled={
                            initializing || busy || checkingDoubleBooking || madeReservation || availability_pending
                        }
                        data-testid="complete-booking-button"
                    />
                    {spinner}
                    {availability_pending && (
                        <div className="payment-action-disabled-reason" role="status" aria-live="polite">
                            {this.props.t('PaymentSection.CheckingAccountPaymentAvailability')}
                        </div>
                    )}
                </div>
            );
        }

        return null;
    },

    closeErrorModal: function () {
        PaymentActions.clearPaymentData();
    },

    renderPaymentError() {
        const { paymentData } = this.state;

        return (
            <MaterialDesignDialog
                open={!!paymentData.error_message}
                onClose={this.closeErrorModal}
                title={'The Payment Failed'}
                content={
                    <div>
                        <div>{paymentData.error_message}</div>
                        <div>Try using a different card.</div>
                    </div>
                }
                actions={[{ onClick: this.closeErrorModal, text: 'Got it' }]}
            />
        );
    },

    onPaymentLinkExpiredGotIt: function () {
        this.setState({ showPaymentLinkExpiredDialog: false });
        this.advanceReservation();
    },

    renderPaymentLinkExpiredDialog() {
        const { t } = this.props;

        return (
            <MaterialDesignDialog
                open={this.state.showPaymentLinkExpiredDialog}
                onClose={() => this.setState({ showPaymentLinkExpiredDialog: false })}
                title=""
                content={<div>{t('ThePaymentLinkHasExpired')}</div>}
                actions={[{ onClick: this.onPaymentLinkExpiredGotIt, text: t('GotIt') }]}
            />
        );
    },

    render() {
        const { t, points, profile, onCouponValidated } = this.props;
        const { reservation_manipulation, reservationData } = this.state;
        const companyId = _.get(profile, 'company.id');
        const bnplEnabled =
            _.get(profile, 'settings.bnpl_enabled', false) || _.get(profile, 'company.settings.bnpl_enabled', false);
        const isElal = isElalCompany();
        const shouldShowElalCoupon = isElal && !COUPON_DISABLED_COMPANY_IDS.includes(companyId);
        const shouldShowCoupons = profile?.coupons?.enabled;

        const PAYMENT_METHODS = this.getPaymentMethodsRender();
        const BOOK_NOW_PAY_LATER_DISCLAIMER = this.getBookNowPayLaterDisclaimer();
        const CHARGE_CURRENCY = this.changeChargeCurrencyRender();
        const DISCLAIMER = this.renderDisclaimer();
        const MARKETING_CONSENT = this.renderMarketingConsent();
        const PAYMENT_METHOD = this.renderPaymentMethod();
        const BNPL_NOTICE = this.renderBnplPaymentNotice();

        const REDEEM_POINTS = this.renderRedeemPoints();

        const payment_action_text = this.getPaymentActionText();
        const PAYMENT_ACTION = this.renderPaymentAction(payment_action_text);
        const PAYMENT_ACTIONS_DIALOG = this.renderPaymentActionsDialog(payment_action_text);
        const DOUBLE_BOOKING_DIALOG = this.renderDoubleBookingDialog();
        const PAYMENT_ERROR_DIALOG = this.renderPaymentError();
        const PAYMENT_LINK_EXPIRED_DIALOG = this.renderPaymentLinkExpiredDialog();

        const form_locked = this.props.shouldLockForm || (reservationData?.status === ReservationConstants.STATUS.INITIALIZING);

        return (
            <div className={`payment-component${isElal ? ' elal' : ''}`} id={ELEMENT_IDS.PAYMENT_COMPONENT}>
                <h4 className="title">{t('PaymentSection.Title')}</h4>

                {(shouldShowElalCoupon || shouldShowCoupons || (points?.show && isElal)) && (
                    <div className="payment-component__redeem-section">
                        {bnplEnabled && (
                            <h5 className="payment-component__redeem-section-title">
                                {t('PaymentSection.RedeemPointsOrDiscountsTitle')}
                            </h5>
                        )}
                        {(shouldShowElalCoupon || shouldShowCoupons) && (
                            <CouponCodeComponent
                                reservation={reservationData}
                                onValidated={onCouponValidated}
                                isFormLocked={form_locked}
                            />
                        )}
                        {points?.show && isElal && (
                            <PointsSliderComponent
                                points={points}
                                profile={profile}
                                deal={this.state.reservationData?.deal || this.props.reservation?.deal}
                                couponValidation={this.props.couponValidation}
                                isFormLocked={form_locked}
                            />
                        )}
                    </div>
                )}

                <div className="payment-details-container">
                    <div className="payment-method-container">
                        {(this.hasMultiplePaymentMethods(false) || this.shouldShowAccountPaymentUnavailableNotice() || (this.props.loyaltyEnabled && !isElal) || this.isBnplEligible()) && (
                            <div className="payment-method-container-inner payment-inputs">
                                {PAYMENT_METHODS}
                                {!this.isBnplEligible() && BOOK_NOW_PAY_LATER_DISCLAIMER}
                                {!this.isBnplEligible() && REDEEM_POINTS}
                                {(!this.isBnplEligible() || this.state.method === PAYMENT_METHOD_TYPES.CREDIT_CARD) && CHARGE_CURRENCY}
                            </div>
                        )}

                        <ReservationManipulation
                            reservation_manipulation={reservation_manipulation}
                            profile={profile}
                            onFailStatusChange={(e) => {
                                this.setState({
                                    reservation_manipulation: {
                                        ...reservation_manipulation,
                                        is_fail: e.target.checked,
                                    },
                                });
                            }}
                            onFailJ5StatusChange={(e) => {
                                this.setState({
                                    reservation_manipulation: {
                                        ...reservation_manipulation,
                                        is_fail_reservation_during_j5: e.target.checked,
                                    },
                                });
                            }}
                            onFailJ4ChargeStatusChange={(e) => {
                                this.setState({
                                    reservation_manipulation: {
                                        ...reservation_manipulation,
                                        is_fail_j4_charge: e.target.checked,
                                    },
                                });
                            }}
                            onPriceChange={(e) => {
                                this.setState({
                                    reservation_manipulation: {
                                        ...reservation_manipulation,
                                        increase_price: e.target.value,
                                    },
                                });
                            }}
                        />

                        <div className="payment-method-container-inner payment-completion">
                            {DISCLAIMER}
                            {MARKETING_CONSENT}
                            {BNPL_NOTICE}
                            {PAYMENT_METHOD}
                            {PAYMENT_ACTION}
                            {PAYMENT_ACTIONS_DIALOG}
                            {DOUBLE_BOOKING_DIALOG}
                            {PAYMENT_ERROR_DIALOG}
                            {PAYMENT_LINK_EXPIRED_DIALOG}
                        </div>
                    </div>
                </div>
            </div>
        );
    },

    _onChange: function () {
        this.setState(getPaymentComponentState(this.state.method, this.isLiveAccountPaymentAvailable()));
    },
});

export default withTranslation()(PaymentComponent);
